import React, { useEffect, useState } from "react";
import dashboardService from "@/services/dashboard.service";
import { useNavigate } from "react-router-dom";
import ResponsivePage from "@/components/layout/ResponsivePage";
import ResponsiveGrid from "@/components/layout/ResponsiveGrid";
import PendingFollowupsAdminCard from "@/components/dashboard/PendingFollowupsAdminCard";

const Card = ({ title, value, color, subtitle, onClick }) => (
  <div
    onClick={onClick}
    style={{
      cursor: onClick ? "pointer" : "default",
      background: "#ffffff",
      padding: "20px 22px",
      borderRadius: 16,
      border: "1px solid #e2e8f0",
      boxShadow: "0 4px 16px -2px rgba(15, 23, 42, 0.04), 0 2px 6px -1px rgba(15, 23, 42, 0.02)",
      display: "flex",
      flexDirection: "column",
      gap: 8,
      position: "relative",
      overflow: "hidden",
      transition: "all 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
    }}
    onMouseEnter={(e) => {
      if (onClick) {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = "0 12px 28px -4px rgba(15, 23, 42, 0.08)";
        e.currentTarget.style.borderColor = "#cbd5e1";
      }
    }}
    onMouseLeave={(e) => {
      if (onClick) {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 4px 16px -2px rgba(15, 23, 42, 0.04), 0 2px 6px -1px rgba(15, 23, 42, 0.02)";
        e.currentTarget.style.borderColor = "#e2e8f0";
      }
    }}
  >
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        background: color,
      }}
    />

    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <span
        style={{
          fontSize: 12,
          color: "#64748b",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.6px",
        }}
      >
        {title}
      </span>
      {onClick && (
        <span
          style={{
            fontSize: 11,
            color: "#6366f1",
            fontWeight: 600,
            background: "#eef2ff",
            padding: "2px 8px",
            borderRadius: 999,
          }}
        >
          View →
        </span>
      )}
    </div>

    <div
      style={{
        fontSize: 28,
        fontWeight: 800,
        color: "#0f172a",
        letterSpacing: "-0.5px",
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      }}
    >
      {value}
    </div>

    {subtitle && (
      <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 500 }}>
        {subtitle}
      </div>
    )}
  </div>
);

const NewDashboard = () => {
  const navigate = useNavigate();

  const [data, setData] = useState({
    revenue: {
      total: 0,
      collected: 0,
    },

    bookings: {
      total: 0,
    },

    leads: {
      total: 0,
      converted: 0,
      conversionRate: 0,
    },
  });

  const [tripStats, setTripStats] = useState({
    starting: 0,
    ending: 0,
    total: 0,
  });

  const [opsStats, setOpsStats] = useState({
    pendingPayments: 0,
    pendingHotelBookings: 0,
    pendingTransportBookings: 0,
    readyTrips: 0,
    totalRevenue: 0,
  });

  const [liveTrips, setLiveTrips] = useState([]);
  const [urgentTrips, setUrgentTrips] = useState([]);

  const [loading, setLoading] = useState(true);

  /* =====================================================
     LOAD DASHBOARD
  ===================================================== */

  useEffect(() => {
    loadDashboard();
  }, []);

  /* =====================================================
     AUTO REFRESH
  ===================================================== */

  useEffect(() => {
    const interval = setInterval(() => {
      loadDashboard();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);

      const res =
        await dashboardService.loadFullDashboard();

      const operationsRes =
        await dashboardService.getOperationsDashboard();

      setData(
        res?.core || {
          revenue: {
            total: 0,
            collected: 0,
          },

          bookings: {
            total: 0,
          },

          leads: {
            total: 0,
            converted: 0,
            conversionRate: 0,
          },
        }
      );

      setTripStats(
        res?.kpis?.trips || {
          starting: 0,
          ending: 0,
          total: 0,
        }
      );

      setOpsStats(
        operationsRes?.stats || {
          pendingPayments: 0,
          pendingHotelBookings: 0,
          pendingTransportBookings: 0,
          readyTrips: 0,
          totalRevenue: 0,
        }
      );

      setLiveTrips(
        Array.isArray(res?.operations?.liveTrips)
          ? res.operations.liveTrips
          : []
      );

      setUrgentTrips(
        Array.isArray(res?.operations?.urgentTrips)
          ? res.operations.urgentTrips
          : []
      );
    } catch (err) {
      console.error(
        "Dashboard load failed:",
        err
      );

      setData({
        revenue: {
          total: 0,
          collected: 0,
        },

        bookings: {
          total: 0,
        },

        leads: {
          total: 0,
          converted: 0,
          conversionRate: 0,
        },
      });

      setTripStats({
        starting: 0,
        ending: 0,
        total: 0,
      });

      setOpsStats({
        pendingPayments: 0,
        pendingHotelBookings: 0,
        pendingTransportBookings: 0,
        readyTrips: 0,
        totalRevenue: 0,
      });

      setLiveTrips([]);
      setUrgentTrips([]);
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     HELPERS
  ===================================================== */

  const currency = (v) =>
    `₹${Number(v || 0).toLocaleString()}`;

  /* =====================================================
     RENDER
  ===================================================== */
return (
    <ResponsivePage
        title="Dashboard"
        subtitle="Live operational overview"
        fullWidth
    >

{loading ? (
    <div
        style={{
            padding:40,
            textAlign:"center"
        }}
    >
        Loading dashboard...
    </div>
) : (

        <>

            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                marginBottom: 24,
                padding: "12px 16px",
                background: "#ffffff",
                borderRadius: 16,
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(15, 23, 42, 0.02)",
              }}
            >
              {[
                "All",
                "New",
                "Contacted",
                "Qualified",
                "Proposal",
                "Negotiation",
                "Confirmed",
              ].map((status, idx) => (
                <div
                  key={status}
                  onClick={() => navigate(status === "All" ? "/leads" : `/leads?status=${status.toLowerCase()}`)}
                  style={{
                    padding: "7px 16px",
                    background: idx === 0 ? "linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)" : "#f8fafc",
                    color: idx === 0 ? "#ffffff" : "#475569",
                    border: idx === 0 ? "none" : "1px solid #e2e8f0",
                    borderRadius: 999,
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 600,
                    transition: "all 0.2s ease",
                    boxShadow: idx === 0 ? "0 4px 12px rgba(79, 70, 229, 0.25)" : "none",
                  }}
                  onMouseEnter={(e) => {
                    if (idx !== 0) {
                      e.currentTarget.style.background = "#eef2ff";
                      e.currentTarget.style.color = "#4f46e5";
                      e.currentTarget.style.borderColor = "#c7d2fe";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (idx !== 0) {
                      e.currentTarget.style.background = "#f8fafc";
                      e.currentTarget.style.color = "#475569";
                      e.currentTarget.style.borderColor = "#e2e8f0";
                    }
                  }}
                >
                  {status}
                </div>
              ))}
            </div>

          {/* =====================================================
              KPI ROW
          ===================================================== */}

<ResponsiveGrid
columns={2}
gap={20}
>

            <Card
              title="Revenue"
              value={currency(data?.revenue?.total)}
              color="#22c55e"
              onClick={() =>
                navigate("/accounts")
              }
            />

            <Card
              title="Leads"
              value={
                data?.leads?.total || 0
              }
              color="#3b82f6"
              onClick={() =>
                navigate("/leads")
              }
            />

            <Card
              title="Bookings"
              value={
                data?.bookings?.total || 0
              }
              color="#a855f7"
              onClick={() =>
                navigate("/bookings")
              }
            />

            <Card
              title="Conversion"
              value={`${data?.leads?.conversionRate || 0}%`}
              color="#f59e0b"
            />
          </ResponsiveGrid>

          {/* =====================================================
              PENDING FOLLOW-UPS ADMIN SECTION
          ===================================================== */}
          <div style={{ marginTop: 24 }}>
            <PendingFollowupsAdminCard />
          </div>

          {/* =====================================================
              TRIP STATS
          ===================================================== */}

            <ResponsiveGrid
             columns={3}
             gap={16}
            >

            <Card
              title="Trips Starting Today"
              value={Number(tripStats?.starting || 0)}
              color="#10b981"
              onClick={() =>
                navigate(
                  "/trips?filter=startingToday"
                )
              }
            />

            <Card
              title="Trips Ending Today"
              value={Number(tripStats?.ending || 0)}
              color="#ef4444"
              onClick={() =>
                navigate(
                  "/trips?filter=endingToday"
                )
              }
            />

            <Card
              title="Total Trips"
              value={Number(tripStats?.total || 0)}
              color="#06b6d4"
              onClick={() =>
                navigate("/trips")
              }
            />
          </ResponsiveGrid>

          {/* =====================================================
              OPERATIONS QUEUES
          ===================================================== */}

<ResponsiveGrid
columns={4}
gap={16}
>

            <Card
              title="Pending Payments"
              value={
                opsStats.pendingPayments
              }
              color="#ef4444"
            />

            <Card
              title="Pending Hotels"
              value={
                opsStats.pendingHotelBookings
              }
              color="#f59e0b"
            />

            <Card
              title="Pending Transport"
              value={
                opsStats.pendingTransportBookings
              }
              color="#8b5cf6"
            />

            <Card
              title="Ready Trips"
              value={opsStats.readyTrips}
              color="#22c55e"
            />
          </ResponsiveGrid>

          {/* =====================================================
              MAIN OPERATIONS GRID
          ===================================================== */}

<ResponsiveGrid
columns={2}
gap={20}
>

            {/* =====================================================
                LIVE OPERATIONS
            ===================================================== */}

            <div
              style={{
                background: "#fff",
                padding: 20,
                borderRadius: 12,
                minHeight: 220,
                boxShadow:
                  "0 2px 8px rgba(0,0,0,0.04)",
              }}
            >
              <h3
                style={{
                  marginBottom: 14,
                  fontWeight: 600,
                }}
              >
                Live Operations
              </h3>

              {!Array.isArray(
                liveTrips
              ) ||
              liveTrips.length === 0 ? (
                <div
                  style={{
                    color: "#888",
                  }}
                >
                  No live trips
                </div>
              ) : (
                liveTrips.map((trip) => (
                  <div
                    key={trip._id}
                    style={{
                      padding: "12px 0",
                      borderBottom:
                        "1px solid #f0f0f0",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      navigate(
                        `/bookings/${trip._id}`
                      )
                    }
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        marginBottom: 4,
                      }}
                    >
                      {trip.customerName ||
                        trip.clientName ||
                        trip.leadName ||
                        "Customer"}
                    </div>

                    <div
                      style={{
                        fontSize: 13,
                        color: "#666",
                      }}
                    >
                      Departure:{" "}
                      {trip.departureDate
                        ? new Date(
                            trip.departureDate
                          ).toLocaleDateString()
                        : "N/A"}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* =====================================================
                URGENT ATTENTION
            ===================================================== */}

            <div
              style={{
                background: "#fff",
                padding: 20,
                borderRadius: 12,
                minHeight: 220,
                boxShadow:
                  "0 2px 8px rgba(0,0,0,0.04)",
              }}
            >
              <h3
                style={{
                  marginBottom: 14,
                  fontWeight: 600,
                }}
              >
                Urgent Attention
              </h3>

              {!urgentTrips.length ? (
                <div
                  style={{
                    color: "#888",
                  }}
                >
                  No urgent trips
                </div>
              ) : (
                urgentTrips.map(
                  (trip) => (
                    <div
                      key={trip._id}
                      style={{
                        padding:
                          "12px 0",
                        borderBottom:
                          "1px solid #f0f0f0",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                        }}
                      >
                        {trip.customerName ||
                          "Customer"}
                      </div>

                      <div
                        style={{
                          fontSize: 13,
                          color: "#666",
                        }}
                      >
                        Departure:{" "}
                        {trip.departureDate
                          ? new Date(
                              trip.departureDate
                            ).toLocaleDateString()
                          : "N/A"}
                     </div>

                    </div>
                  )
                )
              )}
            </div>

          </ResponsiveGrid>

          </>
      )}

    </ResponsivePage>

  );
};

export default NewDashboard;
